import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
    onReset?: () => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class TechnicianErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("TechnicianErrorBoundary caught an error:", error, errorInfo);
        
        // V_CHUNK: Handle ChunkLoadError or dynamically imported module errors from old assets
        const errorMessage = error.message?.toLowerCase() || '';
        const isChunkError = 
            errorMessage.includes('loading chunk') || 
            errorMessage.includes('mime type') || 
            errorMessage.includes('not a valid javascript mime type') ||
            errorMessage.includes('failed to fetch') ||
            errorMessage.includes('dynamically imported module');

        if (isChunkError) {
            console.warn("CRITICAL: Detected obsolete chunk in TechnicianView. Forcing hard reload to clear cache...");
            const lastReload = sessionStorage.getItem('last-chunk-reload');
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
                sessionStorage.setItem('last-chunk-reload', now.toString());
                window.location.reload();
                return;
            }
        }

        this.setState({ error, errorInfo });
    }

    public handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        if (this.props.onReset) {
            this.props.onReset();
        } else {
            window.location.reload();
        }
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-screen bg-white dark:bg-dark-input text-slate-900 dark:text-slate-900 dark:text-white p-6 text-center">
                    <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6 border border-red-500/20 animate-pulse">
                        <AlertTriangle className="w-10 h-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Algo salió mal</h2>
                    <p className="text-slate-400 mb-8 max-w-xs mx-auto">
                        Se ha producido un error inesperado en la vista de técnico. No te preocupes, tus datos deberían estar seguros.
                    </p>

                    <div className="bg-white dark:bg-dark-card p-4 rounded-xl border border-slate-200 dark:border-slate-200 dark:border-dark-border mb-8 w-full max-w-sm text-left overflow-auto max-h-40">
                        <p className="text-xs text-red-400 font-mono break-all">
                            {this.state.error && this.state.error.toString()}
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 w-full max-w-xs">
                        <button
                            onClick={this.handleReset}
                            className="w-full py-3.5 bg-primary hover:bg-blue-600 text-slate-900 dark:text-slate-900 dark:text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-blue-900/30"
                        >
                            <RefreshCw className="w-5 h-5" />
                            Recargar Sesión
                        </button>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="w-full py-3.5 bg-white dark:bg-dark-card hover:bg-slate-100 dark:bg-dark-border text-slate-300 font-bold rounded-xl flex items-center justify-center gap-2 border border-slate-200 dark:border-slate-200 dark:border-dark-border transition-all active:scale-95"
                        >
                            <Home className="w-5 h-5" />
                            Ir al Inicio
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default TechnicianErrorBoundary;
